import {
  useState,
  useEffect,
  createContext,
  useContext,
  ReactNode,
} from 'react';
import authService, { LoginCredentials, User } from '../services/authService';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  error: string | null;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Verificar autenticación al cargar la aplicación
  useEffect(() => {
    const validateSession = async () => {
      console.log('🔍 [useAuth] Iniciando validación de sesión...');
      const storedToken = authService.getToken();
      const storedUser = authService.getCurrentUser();

      console.log('📊 [useAuth] Estado del localStorage:', {
        hasToken: !!storedToken,
        hasUser: !!storedUser,
        tokenPreview: storedToken ? storedToken.substring(0, 30) + '...' : null,
      });

      if (storedToken && storedUser) {
        console.log(
          '🔄 [useAuth] Datos encontrados, validando token contra servidor...'
        );
        try {
          // Validar token contra el servidor ANTES de establecer el estado
          console.log('📡 [useAuth] Llamando a authService.getProfile()...');
          const profile = await authService.getProfile();
          console.log('✅ [useAuth] Token válido! Perfil obtenido:', profile);
          // Solo si la validación es exitosa, establecer los datos
          setToken(storedToken);
          setUser(storedUser);
          console.log(
            '🎯 [useAuth] Estado establecido - isAuthenticated: true'
          );
        } catch (error) {
          // Token inválido o expirado - limpiar datos
          console.log('❌ [useAuth] Token inválido/expirado. Error:', error);
          console.log('🧹 [useAuth] Limpiando sesión...');
          authService.logout();
          console.log('🎯 [useAuth] Sesión limpiada - isAuthenticated: false');
          // No establecer token/user, mantenerlos como null
        }
      } else {
        console.log('ℹ️ [useAuth] No hay datos de sesión en localStorage');
      }

      setIsLoading(false);
      console.log('🏁 [useAuth] Validación completada. isLoading: false');
    };

    validateSession();
  }, []);

  // Función de login usando el servicio
  const login = async (credentials: LoginCredentials): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await authService.login(credentials);

      if (response.success) {
        setToken(response.token);
        setUser(response.user);
        authService.saveSession(response.token, response.user);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Error al iniciar sesión';
      setError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Función de logout
  const logout = (): void => {
    setUser(null);
    setToken(null);
    authService.logout();
  };

  // Limpiar errores
  const clearError = (): void => {
    setError(null);
  };

  const isAuthenticated = !!(user && token);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated,
        isLoading,
        login,
        logout,
        error,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
